import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Calendar, Search, Plus, Grid3x3, List, Wrench, DollarSign } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import StatsCard from '../components/dashboard/StatsCard';
import AssetCard from '../components/dashboard/AssetCard';
import AssetListItem from '../components/dashboard/AssetListItem';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/Select';
import { loadAssets } from '../utils/storage';
import { getUpcomingServices } from '../utils/scheduling';
import { calculateTotalMaintenanceSpent } from '../utils/financial';

const Dashboard = () => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [opacity, setOpacity] = useState(0.3);
  
  // Get full URL for background image (needed for CSS background-image)
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000';
  const backgroundImageUrl = backgroundImage 
    ? (backgroundImage.startsWith('http') ? backgroundImage : `${API_BASE}${backgroundImage}`)
    : null;

  useEffect(() => {
    const fetchAssets = async () => {
      const data = await loadAssets();
      setAssets(data);
      setLoading(false);
    };
    fetchAssets();
  }, []);

  useEffect(() => {
    // Load admin settings (background image and opacity)
    const loadAdminSettings = async () => {
      try {
        const response = await fetch('/api/admin/settings');
        if (response.ok) {
          const settings = await response.json();
          setBackgroundImage(settings.backgroundImage || null);
          setOpacity(settings.opacity !== undefined ? settings.opacity : 0.3);
        }
      } catch (error) {
        console.error('Error loading admin settings:', error);
      }
    };
    loadAdminSettings();
  }, []);

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.type.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = !locationFilter || asset.location === locationFilter;
    return matchesSearch && matchesLocation;
  });

  // Get unique locations for filter dropdown
  const uniqueLocations = Array.from(new Set(assets.map(a => a.location).filter(Boolean))).sort();

  const totalMaintenanceRecords = assets.reduce(
    (sum, asset) => sum + (asset.maintenanceRecords?.length || 0),
    0
  );

  const upcomingServices = getUpcomingServices(assets, 60).length;
  const totalMaintenanceSpent = calculateTotalMaintenanceSpent(assets);

  return (
    <>
      <Navbar />
      {/* Full-screen background */}
      <div 
        className="fixed inset-0 -z-10"
        style={{
          backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
        }}
      >
        {backgroundImageUrl && (
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundColor: `rgba(0, 0, 0, ${1 - opacity})`,
            }}
          />
        )}
      </div>
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto relative z-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <h1 className="text-section text-foreground mb-2">Dashboard</h1>
            <p className="text-body">Manage your household assets and maintenance records</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatsCard
              icon={Package}
              label="Total Assets"
              value={assets.length}
              delay={0}
            />
            <StatsCard
              icon={Wrench}
              label="Upcoming Services"
              value={upcomingServices}
              subtitle={upcomingServices > 0 ? "Next 60 days" : "None scheduled"}
              delay={0.1}
            />
            <StatsCard
              icon={Calendar}
              label="Maintenance Records"
              value={totalMaintenanceRecords}
              delay={0.2}
            />
            <StatsCard
              icon={DollarSign}
              label="Total Maintenance Spent"
              value={`$${totalMaintenanceSpent.toFixed(0)}`}
              delay={0.3}
            />
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Input
                placeholder="Search assets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                icon={<Search className="h-4 w-4" />}
                className="w-full sm:w-64"
              />
              {uniqueLocations.length > 0 && (
                <Select value={locationFilter || 'all'} onValueChange={(value) => setLocationFilter(value === 'all' ? '' : value)}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {uniqueLocations.map(location => (
                      <SelectItem key={location} value={location}>{location}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="flex items-center gap-1 glassmorphic rounded-md p-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className="flex items-center justify-center"
                  title="Grid View"
                >
                  <Grid3x3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="flex items-center justify-center"
                  title="List View"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              <Button 
                onClick={() => navigate('/asset/new')}
                className="flex items-center space-x-2 w-full sm:w-auto"
              >
                <Plus className="h-4 w-4" />
                <span>Add Asset</span>
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center text-body py-12">Loading...</div>
          ) : filteredAssets.length === 0 ? (
            <div className="glassmorphic p-12 text-center">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-medium text-foreground mb-2">
                {searchQuery ? 'No assets found' : 'No assets yet'}
              </h3>
              <p className="text-body mb-4">
                {searchQuery 
                  ? 'Try adjusting your search'
                  : 'Start by adding your first asset'
                }
              </p>
              {!searchQuery && (
                <Button onClick={() => navigate('/asset/new')}>
                  Add Your First Asset
                </Button>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAssets.map((asset, index) => (
                <AssetCard key={asset.id} asset={asset} delay={index * 0.1} />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAssets.map((asset, index) => (
                <AssetListItem key={asset.id} asset={asset} delay={index * 0.05} />
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </>
  );
};

export default Dashboard;

